import { useCareproviderAccountContext } from "context/CareproviderAccountContext";
import {
  DOCUMENT_DATA_PROCESSING_AGREEMENT,
  DOCUMENT_TERMS_AND_CONDITIONS,
  RECARE_INFO_EMAIL,
  TRACK_EVENTS,
} from "core/consts";
import { hasAccessToAdmissions } from "core/model/accounts";
import { hasAccessToReverseSearch } from "core/model/careproviders";
import { Careprovider } from "core/types";
import { DropdownNavbarMenuItem } from "ds/ui/dropdown-navbar/dropdown-navbar";
import { FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { showExtensionsTab } from "dsl/ecosystems/CareproviderSettings/shared";
import { useCareproviderNavigationHandlers } from "dsl/hooks/useNavigationHandlers";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import {
  BedIcon,
  BlocksIcon,
  BookOpenTextIcon,
  BuildingIcon,
  ClipboardListIcon,
  FileCogIcon,
  FlagIcon,
  KeyRoundIcon,
  LinkIcon,
  LogOutIcon,
  MailIcon,
  SearchIcon,
  SmartphoneIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { useTracking } from "react-tracking";
import { useOnLogout } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { useAllowPWA } from "../BrowserNotificationContext/NotificationPermission";
import { useLegalDocuments } from "../LegalDocuments";

type MenuItemsProps = {
  isMobileNavigation?: boolean;
};

type TopMenuItemsProps = MenuItemsProps & {
  careprovider: Careprovider;
};

function useGetTopMenuItems({
  careprovider,
}: TopMenuItemsProps): DropdownNavbarMenuItem[] {
  const { loggedAccount } = useCareproviderAccountContext();
  const navigationHandlers = useCareproviderNavigationHandlers();
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const allowPWA = useAllowPWA();
  const { getDocument } = useLegalDocuments();
  const showNewChip =
    getDocument(DOCUMENT_DATA_PROCESSING_AGREEMENT)?.needs_to_sign ||
    getDocument(DOCUMENT_TERMS_AND_CONDITIONS)?.needs_to_sign;
  const showExtensionTab = showExtensionsTab({ careprovider });
  const hasAdmissionsRole = hasAccessToAdmissions(loggedAccount, careprovider);

  const items: (DropdownNavbarMenuItem | false)[] = [
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/account/*",
      icon: (
        <UserIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "account",
      label: translations.careproviderApp.settings.sidebar.personalDetails,
      // #TODO - https://recare.atlassian.net/browse/DEV-15822 use only href for navigation
      // href: careproviderUrls.goToCareproviderSettingsAccount(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_ACCOUNT });
        navigationHandlers.goToCareproviderSettingsAccount();
      },
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/profile/*",
      icon: (
        <BuildingIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "profile",
      label: translations.careproviderApp.settings.sidebar.companyProfile,
      // href: careproviderUrls.goToCareproviderSettingsCompanyProfile(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_COMPANY });
        navigationHandlers.goToCareproviderSettingsCompanyProfile();
      },
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/services/*",
      icon: (
        <ClipboardListIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "services",
      label: translations.careproviderApp.settings.sidebar.ourServices,
      // href: careproviderUrls.goToCareproviderSettingsServices(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_SERVICES });
        navigationHandlers.goToCareproviderSettingsServices();
      },
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.receiver_settings.steps.services.key,
        tourKey: PRODUCT_TOURS.receiver_settings.key,
      }),
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/team/*",
      icon: (
        <UsersIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "team",
      label: translations.careproviderApp.settings.sidebar.teamManagement,
      // href: careproviderUrls.goToCareproviderSettingsTeam(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_TEAM });
        navigationHandlers.goToCareproviderSettingsTeam();
      },
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/partner-clinics/*",
      icon: (
        <LinkIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "partner_clinics",
      label: translations.careproviderApp.settings.sidebar.partnerClinics,
      // href: careproviderUrls.goToCareproviderSettingsPartnerClinics(),
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_PARTNER_CLINICS,
        });
        navigationHandlers.goToCareproviderSettingsPartnerClinics();
      },
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/legal-documents/*",
      icon: (
        <BookOpenTextIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "legal-documents",
      label: translations.careproviderApp.settings.sidebar.legal,
      // href: careproviderUrls.goToCareproviderSettingsLegalDocuments(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_LEGAL });
        navigationHandlers.goToCareproviderSettingsLegalDocuments();
      },
      showNewChip: showNewChip,
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/capacity/*",
      icon: (
        <BedIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "capacity",
      label: translations.careproviderApp.settings.sidebar.ourCapacity,
      // href: careproviderUrls.goToCareproviderSettingsCapacity(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_CAPACITY });
        navigationHandlers.goToCareproviderSettingsCapacity();
      },
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.receiver_settings.steps.capacity.key,
        tourKey: PRODUCT_TOURS.receiver_settings.key,
      }),
    },
    hasAccessToReverseSearch(careprovider) && {
      hrefMatch: "/careprovider/dashboard/:id/settings/search/*",
      icon: (
        <SearchIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "search",
      label: translations.careproviderApp.settings.sidebar.findPatients,
      // href: careproviderUrls.goToCareproviderSettingsSearch(),
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_SEARCH,
        });
        navigationHandlers.goToCareproviderSettingsSearch();
      },
      tour: tourAttributes({
        stepKey: PRODUCT_TOURS.receiver_settings.steps.reverse_search.key,
        tourKey: PRODUCT_TOURS.receiver_settings.key,
      }),
    },
    {
      hrefMatch: "/careprovider/dashboard/:id/settings/notifications/*",
      icon: (
        <MailIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "notifications",
      label: translations.careproviderApp.settings.sidebar.notifications,
      // href: careproviderUrls.goToCareproviderSettingsNotifications(),
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_NOTIFICATIONS,
        });
        navigationHandlers.goToCareproviderSettingsNotifications();
      },
    },
    hasAdmissionsRole && {
      hrefMatch: "/careprovider/dashboard/:id/settings/admissions/*",
      icon: (
        <FileCogIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "admissions",
      label: translations.careproviderApp.settings.sidebar.settingsAdmissions,
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_ADMISSIONS,
        });
        navigationHandlers.goToCareproviderSettingsAdmissions();
      },
      showNewChip: true,
    },
    !!showExtensionTab && {
      hrefMatch: "/careprovider/dashboard/:id/settings/extensions/*",
      icon: (
        <BlocksIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "extensions",
      label: translations.careproviderApp.settings.extensions.title,

      // href: careproviderUrls.goToCareproviderSettingsExtensions(),
      onPress: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_EXTENSIONS,
        });
        navigationHandlers.goToCareproviderSettingsExtensions();
      },
    },
    allowPWA && {
      hrefMatch: "/careprovider/dashboard/:id/settings/pwa/*",
      icon: (
        <SmartphoneIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "pwa",
      label: translations.pwaSettings.settingsTitle,
      // href: careproviderUrls.goToCareproviderSettingsPWA(),
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_PWA });
        navigationHandlers.goToCareproviderSettingsPWA();
      },
    },
  ];

  return items.truthy();
}

function useGetBottomMenuItems(): DropdownNavbarMenuItem[] {
  const navigationHandlers = useCareproviderNavigationHandlers();
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const onLogout = useOnLogout();

  const items: (DropdownNavbarMenuItem | false)[] = [
    {
      href: `mailto:${RECARE_INFO_EMAIL}?subject=${encodeURIComponent(
        translations.general.reportContent,
      )}`,
      icon: (
        <FlagIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "report_content",
      label: translations.providersearch.footer.reportContent,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.REPORT_CONTENT_CLICKED });
      },
    },
    {
      // href: careproviderUrls.changePassword(),
      icon: (
        <KeyRoundIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "change_password",
      label: translations.careproviderAccount.changePassword,
      onPress: () => {
        trackEvent({ name: TRACK_EVENTS.CHANGE_PASSWORD_CLICKED });
        navigationHandlers.goToChangePassword();
      },
    },
    {
      // href: careproviderUrls.replaceByAuth(),
      icon: (
        <LogOutIcon
          aria-hidden={true}
          className="text-primary"
          size={FONT_SIZE_16}
        />
      ),
      id: "logout",
      label: translations.general.logOut,
      onPress: () => {
        onLogout();
      },
    },
  ];

  return items.truthy();
}

export function useReceiverMenuItems({
  careprovider,
  isMobileNavigation,
}: {
  careprovider: Careprovider;
  isMobileNavigation?: boolean;
}) {
  const menuItemsTop = useGetTopMenuItems({
    careprovider,
    isMobileNavigation,
  });
  const menuItemsBottom = useGetBottomMenuItems();

  return { menuItemsBottom, menuItemsTop };
}
